import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ProductListingDetailsAutoSuggestQuery } from 'bpm/api/graphQL/queries/productSearch.queries'
import { ProductListingDetailsAutoSuggestResult } from 'bpm/api/graphQL/types/product.types'
import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import ExistingProductTypeAutoCompleteInput from './ExistingProductTypeAutoCompleteInput'
import { addProductDialogState } from './addProductFormHandler'

const ProductTypeSection: FC = () => {
    const { sectionNames, firmName } = useReactiveVar(addProductDialogState)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const lastCallInputRef = useRef<Record<string, unknown> | null>(null)
    const [isFallbackResult, setIsFallbackResult] = useState(false)

    const [
        callAutoSuggest,
        { loading: autoSuggestLoading, data: autoSuggestData },
    ] = useLazyQuery<ProductListingDetailsAutoSuggestResult>(
        ProductListingDetailsAutoSuggestQuery,
        {
            fetchPolicy: 'network-only',
            onCompleted: (completedData) => {
                const lastInput = lastCallInputRef.current
                if (!lastInput?.familyName) return

                const hasResults =
                    (completedData?.productListingDetailsAutoSuggest
                        ?.productTypeDetails?.length ?? 0) > 0

                lastCallInputRef.current = null

                if (!hasResults) {
                    setIsFallbackResult(true)
                    callAutoSuggest({
                        variables: {
                            input: { ...lastInput, familyName: '' },
                        },
                    })
                }
            },
            onError: () => {
                lastCallInputRef.current = null
                showGlobalSnackbar({
                    variables: {
                        message: 'Something went wrong with autosuggest',
                        variant: 'error',
                        id: 'autosuggest_Error',
                    },
                })
            },
        }
    )

    const callAutoSuggestWithFallback = useCallback(
        (options) => {
            lastCallInputRef.current = options?.variables?.input ?? null
            setIsFallbackResult(false)
            callAutoSuggest(options)
        },
        [callAutoSuggest]
    )

    const isProductTypeDisabled = useMemo(
        () => !firmName || !sectionNames,
        [firmName, sectionNames]
    )

    return (
        <ExistingProductTypeAutoCompleteInput
            autoSuggestData={autoSuggestData}
            callAutoSuggest={callAutoSuggestWithFallback}
            autoSuggestLoading={autoSuggestLoading}
            disabled={isProductTypeDisabled}
            sectionNames={sectionNames}
            isFallbackResult={isFallbackResult}
        />
    )
}

export default ProductTypeSection
